import { useEffect, useState, useCallback } from "react";
import { callFunction } from "../api/callFunction";
import { toast } from "react-toastify";
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

// Editor for the Gist AI welcome-launcher menu (the tappable tiles shown when a
// member opens "Ask Gist AI"). Reads/writes the server-only
// platformConfig/gistAiLauncher doc through the superAdmin-gated callables; the
// iOS app picks the list up off its next greeting (no app release needed). An
// item is hidden from members when its toggle is off; `prompt` is what tapping
// the tile sends to Gist AI; `systemImage` is an Apple SF Symbol name.

const BUTTON_PRIMARY =
  "inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark disabled:opacity-50";
const BUTTON_SECONDARY =
  "inline-flex items-center gap-x-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50";
const INPUT =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const LABEL = "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1";

function newItem() {
  return {
    id: `item_${Date.now()}`,
    title: "",
    subtitle: "",
    systemImage: "",
    prompt: "",
    enabled: true,
    order: 0,
  };
}

function EnabledToggle({ enabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={enabled ? "Hide from members" : "Show to members"}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          enabled ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export default function GistAiLauncher() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await callFunction("adminGetGistAiLauncher");
      setItems(result?.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load the launcher menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateItem = (index, patch) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const move = (index, delta) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, newItem()]);

  const save = async () => {
    // Mirror the backend's required-field rule up front for a friendlier error
    // than a rejected callable (the greeting reader drops title/prompt-less rows).
    for (let i = 0; i < items.length; i += 1) {
      if (!items[i].title.trim()) {
        toast.error(`Item ${i + 1} needs a title.`);
        return;
      }
      if (!items[i].prompt.trim()) {
        toast.error(`"${items[i].title}" needs a prompt.`);
        return;
      }
    }
    setSaving(true);
    try {
      // order is re-derived from list position server-side; send the array as-is.
      const result = await callFunction("adminUpdateGistAiLauncher", { items });
      setItems(result?.items || items);
      toast.success("Launcher menu saved");
    } catch (err) {
      toast.error(err.message || "Failed to save the launcher menu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Gist AI launcher menu
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The tiles shown when a member opens Ask Gist AI. Changes go live on each
            member's next open — no app update needed. Hidden items are kept but not shown.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-2">
          <button onClick={load} disabled={loading || saving} className={BUTTON_SECONDARY}>
            <ArrowPathIcon className="h-4 w-4" />
            Reload
          </button>
          <button onClick={save} disabled={loading || saving} className={BUTTON_PRIMARY}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((item, index) => (
            <div
              key={item.id || index}
              className={`rounded-lg border p-4 ${
                item.enabled
                  ? "border-gray-200 dark:border-gray-700"
                  : "border-dashed border-gray-300 dark:border-gray-600 opacity-70"
              } bg-white dark:bg-gray-900`}
            >
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-1 pt-1">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ChevronUpIcon className="h-5 w-5" />
                  </button>
                  <span className="text-xs text-gray-400">{index + 1}</span>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ChevronDownIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Title</label>
                    <input
                      className={INPUT}
                      value={item.title}
                      onChange={(e) => updateItem(index, { title: e.target.value })}
                      placeholder="Find events"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Subtitle</label>
                    <input
                      className={INPUT}
                      value={item.subtitle}
                      onChange={(e) => updateItem(index, { subtitle: e.target.value })}
                      placeholder="See what's happening near you"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>SF Symbol (icon)</label>
                    <input
                      className={INPUT}
                      value={item.systemImage}
                      onChange={(e) => updateItem(index, { systemImage: e.target.value })}
                      placeholder="calendar"
                    />
                  </div>
                  <div>
                    <label className={LABEL}>Prompt sent on tap</label>
                    <input
                      className={INPUT}
                      value={item.prompt}
                      onChange={(e) => updateItem(index, { prompt: e.target.value })}
                      placeholder="What's coming up?"
                    />
                  </div>
                </div>

                <div className="flex flex-col items-center gap-3 pt-1">
                  <div className="flex flex-col items-center gap-1">
                    <EnabledToggle
                      enabled={item.enabled}
                      onToggle={() => updateItem(index, { enabled: !item.enabled })}
                    />
                    <span className="text-[11px] text-gray-400">
                      {item.enabled ? "Shown" : "Hidden"}
                    </span>
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    aria-label="Delete item"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          <button onClick={addItem} className={BUTTON_SECONDARY}>
            <PlusIcon className="h-4 w-4" />
            Add item
          </button>
        </div>
      )}
    </div>
  );
}
